import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Module-level cache — survives component unmount/remount within the same browser session.
 * All admin pages share the same cached facilities list so navigating between pages
 * never triggers a duplicate fetch.
 *
 * The cache is invalidated only when the user explicitly mutates facilities
 * (AdminFacilities calls invalidateFacilitiesCache() after save/delete).
 */
let _cache = null;
let _promise = null; // in-flight request deduplication

export function invalidateFacilitiesCache() {
  _cache = null;
  _promise = null;
}

/**
 * Drop-in replacement for the inline `api.get('/api/facilities/')` pattern.
 * Returns { facilities, loading } — identical shape to what pages already use.
 */
export function useFacilities() {
  const { api } = useAuth();
  const [facilities, setFacilities] = useState(() => _cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) {
      setFacilities(_cache);
      setLoading(false);
      return;
    }

    // Deduplicate: if another component already kicked off the fetch, piggyback on it.
    if (!_promise) {
      _promise = api.get('/api/facilities/')
        .then(r => {
          _cache = r.data;
          return r.data;
        })
        .catch(() => {
          _promise = null; // allow retry on next mount
          return [];
        });
    }

    _promise.then(data => {
      setFacilities(data);
      setLoading(false);
    });
  }, [api]);

  return { facilities, loading };
}
